import React, { useEffect, useState } from 'react';
import { Check, ChevronDown, LoaderCircle, Save, SlidersHorizontal } from 'lucide-react';

export default function BreakSettingsEditor({ settings, onSave, saving, feedback }) {
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!saved) return undefined;
    const timeout = setTimeout(() => setSaved(false), 4000);
    return () => clearTimeout(timeout);
  }, [saved]);
  useEffect(() => {
    if (!dirty) setDraft({ minutes: String(settings.breakLimitMinutes),
      teams: (settings.teams || []).map((row) => ({ team: row.team,
        useDefault: row.breakLimitMinutes == null,
        minutes: row.breakLimitMinutes == null ? '' : String(row.breakLimitMinutes) })) });
  }, [settings, dirty]);
  if (!draft) return null;
  const customCount = (settings.teams || []).filter((row) => row.breakLimitMinutes != null).length;
  const hasChanges = dirty && (Number(draft.minutes) !== Number(settings.breakLimitMinutes) ||
    draft.teams.some((row) => {
      const original = (settings.teams || []).find((item) => item.team === row.team);
      return row.useDefault !== (original?.breakLimitMinutes == null) ||
        (!row.useDefault && (row.minutes.trim() === '' || Number(row.minutes) !== Number(original?.breakLimitMinutes)));
    }));
  const change = (next) => { setDraft(next); setDirty(true); setError(''); setSaved(false); };
  const submit = async (event) => {
    event.preventDefault();
    const valid = (value) => value.trim() !== '' && Number.isInteger(Number(value)) &&
      Number(value) >= settings.minBreakLimitMinutes && Number(value) <= settings.maxBreakLimitMinutes;
    if (!valid(draft.minutes) || draft.teams.some((row) => !row.useDefault && !valid(row.minutes))) {
      setError(`Enter whole minutes from ${settings.minBreakLimitMinutes} to ${settings.maxBreakLimitMinutes}.`);
      return;
    }
    try {
      await onSave({ breakLimitMinutes: Number(draft.minutes), teamOverrides: draft.teams.map((row) => ({
        team: row.team, breakLimitMinutes: row.useDefault ? null : Number(row.minutes)
      })) });
      setDirty(false);
      setSaved(true);
    } catch { /* Parent owns the API error message. Keep the unsaved draft. */ }
  };
  return (
    <details className="team-break-settings">
      <summary>
        <SlidersHorizontal size={20} aria-hidden="true" />
        <span><strong>Break Settings</strong><small>Default {settings.breakLimitMinutes} min
          <span aria-hidden="true"> / </span>{customCount} custom override{customCount === 1 ? '' : 's'}</small></span>
        <ChevronDown className="break-settings-chevron" size={18} aria-hidden="true" />
      </summary>
      <form onSubmit={submit}>
        <div className="break-default-row">
          <label htmlFor="default-break-minutes">Default Break Limit</label>
          <div className="break-number-field">
          <input id="default-break-minutes" aria-label="Default break limit in minutes" type="number" step="1"
            min={settings.minBreakLimitMinutes} max={settings.maxBreakLimitMinutes} value={draft.minutes}
            disabled={saving} onChange={(event) => change({ ...draft, minutes: event.target.value })} />
          <span>min</span></div>
        </div>
        <h3 className="break-team-section-title">Team Overrides</h3>
        {draft.teams.map((row, index) => (
          <div key={row.team} className={`team-break-setting-row ${row.useDefault ? 'is-default' : 'is-custom'}`}>
            <div className="break-team-heading">
              <label htmlFor={`team-break-${index}`}>{row.team}</label>
              <span className={`break-policy-badge ${row.useDefault ? 'default' : 'custom'}`}>{row.useDefault ? 'Default' : 'Custom'}</span>
            </div>
            <div className="break-team-controls">
            <div className="break-number-field">
            <input id={`team-break-${index}`} aria-label={`${row.team} break limit in minutes`} type="number" step="1"
              min={settings.minBreakLimitMinutes} max={settings.maxBreakLimitMinutes}
              placeholder={draft.minutes} disabled={saving || row.useDefault} value={row.minutes}
              onChange={(event) => change({ ...draft, teams: draft.teams.map((item, i) => i === index ? { ...item, minutes: event.target.value } : item) })} />
            <span>min</span></div>
            <label className="team-break-default">
              <span>Use default</span>
              <input type="checkbox" role="switch" aria-label={`Use default for ${row.team}`} checked={row.useDefault} disabled={saving}
                onChange={(event) => change({ ...draft, teams: draft.teams.map((item, i) => i === index ?
                  { ...item, useDefault: event.target.checked, minutes: event.target.checked ? '' : draft.minutes } : item) })} />
              <span className="break-switch-track" aria-hidden="true" />
            </label>
            </div>
            <p className="break-effective">Effective limit <strong>{(row.useDefault ? draft.minutes : row.minutes) || '-'} min</strong></p>
          </div>
        ))}
        <div className="break-settings-footer">
          <button type="submit" disabled={saving || !hasChanges}>
            {saving ? <LoaderCircle className="break-save-spinner" size={16} aria-hidden="true" /> : <Save size={16} aria-hidden="true" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {saved && !dirty && <span className="break-save-success" role="status"><Check size={16} aria-hidden="true" />Changes saved</span>}
          {hasChanges && !saving && <span className="break-unsaved">Unsaved changes</span>}
        </div>
        {(error || (feedback && feedback !== 'Break settings saved.')) && <p className="break-save-error" role="status">{error || feedback}</p>}
      </form>
    </details>
  );
}
